import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { contractTemplateService } from "@/services/contractTemplateService";
import type {
  ContractTemplateCreatePayload,
  ContractTemplateUpdatePayload,
} from "@/types/contractTemplate";

const keys = {
  list: (wsId: number, onlyActive?: boolean) =>
    ["contract-templates", wsId, onlyActive ?? "all"] as const,
};

export function useContractTemplates(
  workspaceId: number | null,
  opts?: { onlyActive?: boolean },
) {
  const onlyActive = opts?.onlyActive;
  return useQuery({
    queryKey: keys.list(workspaceId ?? 0, onlyActive),
    queryFn: () =>
      contractTemplateService.list(
        workspaceId as number,
        onlyActive ? { only_active: true } : undefined,
      ),
    enabled: workspaceId != null,
  });
}

export function useCreateContractTemplate(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ContractTemplateCreatePayload) =>
      contractTemplateService.create(workspaceId, payload),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["contract-templates", workspaceId] }),
  });
}

export function useUpdateContractTemplate(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ContractTemplateUpdatePayload }) =>
      contractTemplateService.update(workspaceId, id, payload),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["contract-templates", workspaceId] }),
  });
}

export function useDeleteContractTemplate(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => contractTemplateService.remove(workspaceId, id),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["contract-templates", workspaceId] }),
  });
}
